import { useState, useEffect, useCallback } from 'react';
import { useOrientation } from './useOrientation';
import {
  ENV_FIT_MODE,
  FitMode,
  MAX_DEFORMATION_PCT,
} from '~/utils/viewportFit';

const LANDSCAPE_WIDTH = 1920;
const LANDSCAPE_HEIGHT = 1080;
const PORTRAIT_WIDTH = 1080;
const PORTRAIT_HEIGHT = 1920;

export interface ViewportScale {
  /** Scale horizontal. Igual a scaleY em modos contain/cover; diferente em stretch. */
  scaleX: number;
  /** Scale vertical. */
  scaleY: number;
  /** Largura do design (1920 em landscape, 1080 em portrait). */
  width: number;
  /** Altura do design (1080 em landscape, 1920 em portrait). */
  height: number;
  /**
   * Modo efetivamente aplicado. Pode diferir do solicitado quando `stretch`
   * excede `MAX_DEFORMATION_PCT` → fallback automático pra `contain`.
   */
  effectiveMode: FitMode;
}

/**
 * Calcula a escala viewport→design baseado no modo de renderização.
 *
 * @param overrideMode — quando fornecido, força um modo específico ignorando
 *   `ENV_FIT_MODE`. Uso atual: `ScaledViewport` força `'contain'` em
 *   Android+kiosk OFF pra preservar status/nav bars do Android Settings.
 *
 * @param overrideMaxPct — limite custom de deformação em stretch. Default
 *   `MAX_DEFORMATION_PCT` (10%). Reservado pra Fase 3 do roadmap
 *   (configurável por tenant).
 *
 * @returns `{ scaleX, scaleY, width, height, effectiveMode }`. `scaleX === scaleY`
 *   exceto em `stretch`.
 */
export const useViewportScale = (
  overrideMode?: FitMode,
  overrideMaxPct?: number,
  customDesignWidth?: number | null,
  customDesignHeight?: number | null,
): ViewportScale => {
  const { orientation } = useOrientation();

  const calculateScale = useCallback((): ViewportScale => {
    const designWidth =
      customDesignWidth && customDesignWidth > 0
        ? customDesignWidth
        : orientation === 'portrait'
          ? PORTRAIT_WIDTH
          : LANDSCAPE_WIDTH;
    const designHeight =
      customDesignHeight && customDesignHeight > 0
        ? customDesignHeight
        : orientation === 'portrait'
          ? PORTRAIT_HEIGHT
          : LANDSCAPE_HEIGHT;

    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    const scaleX_raw = windowWidth / designWidth;
    const scaleY_raw = windowHeight / designHeight;

    const mode: FitMode = overrideMode ?? ENV_FIT_MODE;
    // Embedded: o app HOST controla o tamanho da View arbitrariamente, então
    // `stretch` deve PREENCHER de verdade (sem o clamp de 10% que protege a frota
    // de telas ~16:9). Sem isto, um container com aspect >10% fora do design
    // letterboxa (barra preta) — não é o esperado quando o host escolheu o tamanho.
    // Gated por VITE_PLATFORM==='embedded' → frota/outros alvos INALTERADOS.
    const embeddedDefaultMaxPct =
      import.meta.env.VITE_PLATFORM === 'embedded'
        ? Number.POSITIVE_INFINITY
        : MAX_DEFORMATION_PCT;
    const maxPct = overrideMaxPct ?? embeddedDefaultMaxPct;

    if (mode === 'contain') {
      const s = Math.min(scaleX_raw, scaleY_raw);
      return {
        scaleX: s,
        scaleY: s,
        width: designWidth,
        height: designHeight,
        effectiveMode: 'contain',
      };
    }

    if (mode === 'cover') {
      const s = Math.max(scaleX_raw, scaleY_raw);
      return {
        scaleX: s,
        scaleY: s,
        width: designWidth,
        height: designHeight,
        effectiveMode: 'cover',
      };
    }

    // stretch
    const max = Math.max(scaleX_raw, scaleY_raw);
    const min = Math.min(scaleX_raw, scaleY_raw);
    const deformationPct = (max / min - 1) * 100;

    if (deformationPct <= maxPct) {
      // Dentro do limite — estica 100% (sem letterbox).
      return {
        scaleX: scaleX_raw,
        scaleY: scaleY_raw,
        width: designWidth,
        height: designHeight,
        effectiveMode: 'stretch',
      };
    }

    // Excede limite — trava em maxPct, letterbox no excedente.
    const clamped = min * (1 + maxPct / 100);
    const scaleX = scaleX_raw < scaleY_raw ? min : clamped;
    const scaleY = scaleY_raw < scaleX_raw ? min : clamped;
    return {
      scaleX,
      scaleY,
      width: designWidth,
      height: designHeight,
      effectiveMode: 'stretch',
    };
  }, [
    orientation,
    overrideMode,
    overrideMaxPct,
    customDesignWidth,
    customDesignHeight,
  ]);

  const [viewport, setViewport] = useState<ViewportScale>(calculateScale);

  useEffect(() => {
    const handleResize = (): void => {
      setViewport(calculateScale());
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateScale]);

  return viewport;
};
